import { cva, type VariantProps } from 'class-variance-authority';
import { cn, focusRing } from '@/lib/utils';

/* Single source of variant truth for every button-like surface
 * (Button, ButtonLink, ButtonAnchor). Keeps internal/external links
 * visually identical while letting each component pick the right
 * underlying element for semantics + a11y.
 */
export const buttonStyles = cva(
  cn(
    'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md font-medium leading-none',
    'transition-[transform,box-shadow,background-color,color] duration-150',
    // touch-action: manipulation removes the 300ms double-tap delay on mobile
    // so primary CTAs feel snappy on phones.
    'touch-manipulation',
    'disabled:pointer-events-none disabled:opacity-50',
    focusRing,
  ),
  {
    variants: {
      variant: {
        primary: cn(
          'bg-gradient-brand text-primary-foreground shadow-glow-primary',
          'hover:-translate-y-0.5 hover:shadow-glow-violet hover:ring-2 hover:ring-primary/30',
          'active:translate-y-0 active:scale-[0.98]',
        ),
        secondary: cn(
          'border border-border bg-surface text-fg',
          'hover:border-border-strong hover:bg-surface-alt',
        ),
        outline: 'border border-border bg-transparent text-fg hover:bg-surface',
        ghost: 'bg-transparent text-fg-muted hover:bg-surface hover:text-fg',
        link: 'bg-transparent text-primary underline-offset-4 hover:underline',
      },
      size: {
        sm: 'h-9 px-3 text-sm',
        md: 'h-11 px-5 text-sm',
        lg: 'h-12 px-6 text-base',
        icon: 'size-10 p-0',
      },
    },
    defaultVariants: {
      variant: 'primary',
      size: 'md',
    },
  },
);

export type ButtonVariantProps = VariantProps<typeof buttonStyles>;
export type ButtonStyleVariant = NonNullable<ButtonVariantProps['variant']>;
export type ButtonStyleSize = NonNullable<ButtonVariantProps['size']>;
